<template>
  <v-card class="mx-auto">
    <v-stepper
      v-model="step"
      dark
      :hidden="step === 999"
      class="elevation-0 secondary"
      :class="{ changeLabelDirection: $vuetify.breakpoint.smAndDown }"
    >
      <v-stepper-header class="elevation-0">
        <v-stepper-step
          :complete="step > 1"
          color="white"
          :rules="[() => !stepIsInError]"
          step="1"
        >
          {{ $t('register.title.steppers.emailPassword') }}
        </v-stepper-step>

        <v-divider class="white"></v-divider>

        <v-stepper-step
          :complete="step > 2"
          color="white"
          :rules="[() => !stepIsInError]"
          step="2"
        >
          {{ $t('register.title.steppers.validatePhone') }}
        </v-stepper-step>
      </v-stepper-header>
    </v-stepper>

    <v-window v-model="step" touchless>
      <v-window-item :value="1">
        <v-form
          ref="formStep1"
          v-model="validStep1"
          v-on:submit.prevent="
            () => {
              return false;
            }
          "
        >
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12>
                <v-text-field
                  :label="$t('global.placeholder.username')"
                  v-model="form.step1.username"
                  class="mx-2"
                  :rules="usernameRules"
                  type="email"
                  required
                >
                </v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex xs16>
                <v-text-field
                  :label="$t('global.placeholder.password')"
                  v-model="form.step1.password"
                  class="mx-2"
                  :rules="passwordRules"
                  :append-icon="hidePassword ? 'visibility' : 'visibility_off'"
                  @click:append="() => (hidePassword = !hidePassword)"
                  :type="hidePassword ? 'text' : 'password'"
                  required
                >
                </v-text-field>
              </v-flex>
              <v-flex xs16>
                <v-text-field
                  :label="$t('global.placeholder.repeatPassword')"
                  v-model="form.step1.repeatPassword"
                  class="mx-2"
                  :rules="passwordRules"
                  :append-icon="
                    hideRepeatPassword ? 'visibility' : 'visibility_off'
                  "
                  @click:append="
                    () => (hideRepeatPassword = !hideRepeatPassword)
                  "
                  :type="hideRepeatPassword ? 'text' : 'password'"
                  required
                >
                </v-text-field>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 v-show="withRegistrationCode">
                <v-text-field
                  :label="$t('register.placeholder.registrationCode')"
                  v-model="form.step1.registrationCode"
                  ref="registrationCode"
                  type="text"
                  class="mx-2"
                  append-outer-icon="close"
                  @click:append-outer="toggleWithRegistrationCode"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 v-show="!withRegistrationCode">
                <v-switch
                  v-model="withRegistrationCode"
                  @change="setFocus('registrationCode')"
                  color="primary"
                  class="mx-2"
                  :label="$t('register.switch.registrationCode')"
                ></v-switch>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 v-show="withGodfather">
                <v-text-field
                  :label="$t('register.placeholder.godfather')"
                  v-model="form.step1.godfather"
                  ref="godfather"
                  class="mx-2"
                  :rules="godfatherRules"
                  type="email"
                  append-outer-icon="close"
                  @click:append-outer="toggleWithGodfather"
                >
                </v-text-field>
              </v-flex>
              <v-flex xs12 v-show="!withGodfather">
                <v-switch
                  v-model="withGodfather"
                  @change="setFocus('godfather')"
                  color="primary"
                  class="mx-2"
                  :label="$t('register.switch.godfather')"
                ></v-switch>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-form>
      </v-window-item>

      <v-window-item :value="2">
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12>
              <v-text-field
                :label="$t('global.placeholder.username')"
                v-model="form.step1.username"
                class="mx-2"
                :readonly="true"
                type="email"
              >
              </v-text-field>
            </v-flex>
            <v-flex xs12>
              <v-form
                ref="formStep2"
                v-model="validStep2"
                v-on:submit.prevent="
                  () => {
                    return false;
                  }
                "
              >
                <edit-phone-number
                  @on-phone-updated="onPhoneUpdated"
                  ref="editPhoneNumber"
                ></edit-phone-number>
              </v-form>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-window-item>

      <v-window-item :value="999">
        <v-card-text>
          <v-layout row>
            <v-flex xs12>
              <h1
                class="font-weight-medium grey--text grey--darken-2 mt-3 ml-2 mb-4 title"
              >
                {{ errors[0] }}
              </h1>

              <template v-if="isRemoved">
                <advice color="error" class="mb-4">
                  <p
                    class="subheading grey--text grey--ar-1 ma-0"
                    v-html="$t('register.advice.removed')"
                  ></p>
                </advice>

                <advice color="primary">
                  <p
                    class="subheading grey--text grey--ar-1 ma-0"
                    v-html="
                      $t('register.advice.contact', {
                        email: contactEmail,
                        username: form.step1.username,
                        phone: form.step2.phone,
                      })
                    "
                  ></p>
                </advice>
              </template>

              <template v-else-if="isBanned">
                <advice color="error" class="mb-4">
                  <p
                    class="subheading grey--text grey--ar-1 ma-0"
                    v-html="$t('register.advice.banned')"
                  ></p>
                </advice>

                <advice color="primary">
                  <p
                    class="subheading grey--text grey--ar-1 ma-0"
                    v-html="
                      $t('register.advice.contact', {
                        email: contactEmail,
                        username: form.step1.username,
                        phone: form.step2.phone,
                      })
                    "
                  ></p>
                </advice>
              </template>

              <template v-else-if="isNotActived">
                <advice color="warning">
                  <p
                    class="subheading grey--text grey--ar-1 ma-0"
                    v-html="$t('register.advice.alreadyRegistered')"
                  ></p>
                </advice>
              </template>

              <template v-else>
                <advice color="error">
                  <p
                    class="subheading grey--text grey--ar-1 ma-0"
                    v-html="$t('register.advice.alreadyRegistered')"
                  ></p>
                </advice>
              </template>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-window-item>
    </v-window>

    <v-card-actions class="pb-3">
      <v-spacer></v-spacer>

      <template v-if="step === 999">
        <v-btn
          flat
          color="primary"
          class="initialize"
          round
          outline
          :disabled="isLoadingStep"
          @click="cancelValidationGoBack"
        >
          {{ $t('register.button.tryAgain') }}
        </v-btn>
      </template>

      <template v-if="isNotActived && !stepShowBack">
        <v-btn
          flat
          color="accent"
          class="initialize"
          outline
          round
          :disabled="isLoadingStep"
          :to="{ path: $t('urlLogin') }"
        >
          {{ $t('site.menu.login') }}
        </v-btn>
      </template>

      <template v-if="!isNotActived && stepShowBack">
        <v-btn
          flat
          class="initialize"
          color="primary"
          round
          @click="cancelValidationGoBack"
        >
          {{ $t('global.action.back') }}
        </v-btn>
      </template>

      <template v-if="showLogoutAndStart">
        <v-btn
          flat
          color="primary"
          class="initialize"
          round
          :disabled="isLoadingStep"
          @click="logoutAndStart"
        >
          {{ $t('register.button.tryAgain') }}
        </v-btn>
      </template>

      <v-btn
        round
        v-show="showResendVerificationCode"
        outline
        color="accent"
        class="initialize right"
        :disabled="!validStep1"
        :loading="isLoadingStep"
        @click="onResend"
      >
        {{ $t('global.button.resend') }}
      </v-btn>

      <v-btn
        round
        id="verify-phone-number"
        v-show="verifyPhoneCode"
        outline
        color="accent"
        class="initialize right"
        :disabled="!validStep1"
        :loading="isLoadingStep"
        @click="onNextStep"
      >
        {{ $t('global.button.verify') }}
      </v-btn>

      <v-btn
        round
        v-if="step === 1"
        outline
        color="accent"
        class="initialize right"
        type="submit"
        :disabled="!validStep1"
        :loading="isLoadingStep"
        @click="onNextStep"
      >
        {{ $t('global.button.next') }}
      </v-btn>

      <v-btn
        round
        v-if="step === 2 && !verifyPhoneCode"
        outline
        color="accent"
        class="initialize right"
        type="submit"
        :disabled="!validStep2"
        :loading="isLoadingStep"
        @click="onNextStep"
      >
        {{ $t('register.button.sentSms') }}
      </v-btn>
    </v-card-actions>

    <v-card-actions class="py-4 grey lighten-4 mt-5" v-if="showFooter">
      <v-spacer></v-spacer>
      <span class="grey--text text--darken-2">{{
        $t('register.label.login')
      }}</span>
      <v-btn
        :to="$t('urlLogin')"
        class="ml-3 initialize"
        color="primary"
        round
        outline
        v-html="$t('register.button.login')"
      ></v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import store from '@/store';
import EditPhoneNumber from '@/components/commons/EditPhoneNumber';
import Advice from '@/components/commons/Advice';
import registerService from '@/services/registerService';
import _ from 'lodash';
import manageErrorService from '@/services/manageErrorService';
import sessionService from '@/services/sessionService';
import keys from '@/store/keys';
import { EventBus } from '@/services/eventBus';
import profileService from '@/services/profileService';
import authService from '../../services/authService';

export default {
  name: 'Register',
  components: {
    [Advice.name]: Advice,
    [EditPhoneNumber.name]: EditPhoneNumber,
  },
  computed: {
    showFooter() {
      return !this.isLogged && this.step !== 999;
    },
    showLogoutAndStart() {
      return this.step === 2 && this.isLogged && !this.verifyPhoneCode;
    },
    stepIsInError() {
      return this.step === 999;
    },
    stepShowBack() {
      if (store.state.isLogged) {
        return this.verifyPhoneCode;
      }
      return this.step > 1 && !this.stepIsInError;
    },
    currentTitle() {
      switch (this.step) {
        case 1:
          return this.$i18n.t('register.title.step.1');
        case 2:
          return this.$i18n.t('register.title.step.2');
        case 999:
          return this.$i18n.t('register.title.step.error');
        default:
          return this.$i18n.t('register.title.step.3');
      }
    },
    usernameRules() {
      return [
        (v) => !!v || this.$i18n.t('global.validation.required.text'),
        (v) =>
          /^\w+([+.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(v) ||
          this.$i18n.t('global.validation.email.format'),
      ];
    },
    godfatherRules() {
      if (this.withGodfather === true) {
        return [
          (v) =>
            /^\w+([+.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(v) ||
            this.$i18n.t('global.validation.email.format'),
        ];
      } else {
        return [];
      }
    },
    passwordRules() {
      return [
        (v) => !!v || this.$i18n.t('global.validation.required.text'),
        (v) => v.length >= 4 || this.$i18n.t('global.validation.minimum.4'),
        (v) => v.length <= 50 || this.$i18n.t('global.validation.maximum.50'),
        this.form.step1.password === this.form.step1.repeatPassword ||
          this.$i18n.t('global.validation.password.equals'),
      ];
    },
    isBanned() {
      return ['BANNED', 'SANCTIONED'].indexOf(this.errorType) > -1;
    },
    isRemoved() {
      return ['REMOVED', 'GDPR'].indexOf(this.errorType) > -1;
    },
    isNotActived() {
      return this.errorType === 'NOTACTIVED';
    },
    isLogged() {
      return store.state.isLogged;
    },
    contactEmail() {
      return store.state.countrySelected.contact.email;
    },
    registeredWithBle() {
      return store.state.user.registeredWithBleDateLocal;
    },
  },
  data() {
    return {
      withGodfather: false,
      withRegistrationCode: false,
      showResendVerificationCode: false,
      /* 1-inicio registro, 2-validar el teléfono, 3-activar el teletag */
      step: 1,
      validStep1: false,
      validStep2: false,
      isLoadingStep: false,
      hidePassword: false,
      hideRepeatPassword: false,
      form: {
        step1: {
          username: '',
          password: '',
          repeatPassword: '',
          godfather: '',
          registrationCode: '',
        },
        step2: {
          prefix: store.state.countrySelected.prefix,
          phone: '',
        },
      },
      verificationCode: '',
      verificationId: '',
      errors: [],
      errorType: '',
      verifyPhoneCode: false,
    };
  },
  mounted() {
    this.goToStep();
  },
  created() {
    if (
      this.form.step1.godfather === '' &&
      typeof this.$route.query.godfather !== 'undefined'
    ) {
      this.form.step1.godfather = this.$route.query.godfather;
      this.withGodfather = true;
    }
  },
  methods: {
    onPhoneUpdated(params) {
      this.form.step2.prefix = `${params.prefix}`;
      this.form.step2.phone = params.phone;
    },
    goToStep() {
      let user = store.state.user;
      if (user !== null) {
        if (user.status === 'NO_PHONE_VALIDATED') {
          this.form.step1.username = store.state.user.username;
          // Es para que pasen las validaciones: 'Th3C4t'
          this.form.step1.password = 'Th3C4t';
          this.form.step1.repeatPassword = 'Th3C4t';

          this.$nextTick(() => {
            this.$refs.editPhoneNumber.setValues({
              prefix: Number(store.state.user.prefix),
              phone: store.state.user.phone,
            });

            this.step = 2;
          });
        } else if (user.status === 'REQUEST_LETTER') {
          if (this.registeredWithBle) {
            this.$router.push({ path: this.$i18n.t('urlValidationWait') });
          } else {
            this.$router.push({ path: this.$i18n.t('urlTeletac') });
          }
        } else if (user.status === 'NO_TELETAG_ACTIVED') {
          if (this.registeredWithBle) {
            this.$router.push({ path: this.$i18n.t('urlValidationWait') });
          } else {
            this.$router.push({ path: this.$i18n.t('urlTeletac') });
          }
        } else if (user.status === 'ACTIVED') {
          this.$router.push({ path: this.$i18n.t('urlUser') });
        } else {
        }
      } else {
        this.$mam.eventRegisterStep1();
      }
    },
    cancelValidationGoBack() {
      this.$refs.editPhoneNumber.goToStep1();
      // this.$refs.editPhoneNumber.reset();
      this.verifyPhoneCode = false;
      this.errorType = null;
      if (store.state.isLogged) {
        this.step = 2;
      } else {
        this.step = 1;
      }
    },
    async onNextStep() {
      this.isLoadingStep = true;
      if (this.verifyPhoneCode) {
        await this.onVerifyPhoneCode();
        this.$mam.eventRegisterStep3();
        return;
      }
      if (this.step === 1) {
        this.$mam.eventRegisterStep1Email();

        let params = {
          email: this.form.step1.username,
        };
        registerService.traceUsername(params);

        this.step++;
      } else if (this.step === 2) {
        if (store.state.isLogged) {
          await this.updatePhoneNumber();
        } else {
          await this.enroll();
        }
      }
      this.isLoadingStep = false;
    },
    async enroll() {
      let isValidate = await this.$refs.formStep2.validate();
      if (!isValidate) {
        return false;
      }

      let params = {
        username: this.form.step1.username,
        password: this.form.step1.password,
        godfather: this.form.step1.godfather,
        origin: this.form.step1.registrationCode,
        phone: this.form.step2.phone,
        prefix: `+${this.form.step2.prefix}`,
        city: `CITY__${store.state.citySelected.code}`,
        withOrignGodFatherVersion: true,
        useFirebase: true,
      };

      let results = await registerService.enroll(params);
      if (results.error) {
        if (typeof results.error.errors !== 'undefined') {
          manageErrorService.handle(results.data);
        } else {
          this.errors = _.map(results.data.errorKeywords, (e) => {
            return this.$i18n.t(
              `global.register.error.${e.type.toLowerCase()}.${e.status.toLowerCase()}`
            );
          });

          if (results.data.errorKeywords) {
            this.errorType = results.data.errorKeywords[0].status;
          }

          this.step = 999;
        }
        this.isLoadingStep = false;
        return;
      }

      await this.$refs.editPhoneNumber.update(results.data.firebaseToken);
      this.verifyPhoneCode = true;

      this.$mam.loginNewUser();
      this.$mam.eventRegisterStep2();
    },
    async updatePhoneNumber() {
      let params = {
        phone: this.form.step2.phone,
        prefix: `+${this.form.step2.prefix}`,
      };

      let results = await profileService.updatePhone(params);
      if (results.error) {
        let error = manageErrorService.handle(results.data);
        this.errors.length = 0;
        this.errors.push(error);
        this.step = 999;
        this.isLoadingStep = false;
        return;
      }

      let resultsCheck = await sessionService.check();
      await this.$refs.editPhoneNumber.update(resultsCheck.data.customToken);

      this.verifyPhoneCode = true;
    },
    async onVerifyPhoneCode() {
      let result = await this.$refs.editPhoneNumber.onVerifyPhoneCode();
      if (result) {
        await sessionService.check(true);
        if (this.registeredWithBle) {
          this.$router.push({ path: this.$i18n.t('urlValidationWait') });
        } else {
          this.$router.push({ path: this.$i18n.t('urlTeletac') });
        }
      } else {
        EventBus.$emit(keys.eventOnShowNotification, {
          message: 'register.phone.code.invalid',
          type: 'error',
          doNotTranslate: false,
        });
        this.isLoading = false;
        this.showResendVerificationCode = true;
      }

      this.isLoadingStep = false;
    },
    async onResend() {
      this.isLoadingStep = true;
      let results = await sessionService.check(true);
      await this.$refs.editPhoneNumber.update(results.data.customToken);
      this.verifyPhoneCode = true;
      this.showResendVerificationCode = false;
      this.isLoadingStep = false;
    },
    toggleWithGodfather() {
      this.form.step1.godfather = '';
      this.withGodfather = false;
    },
    toggleWithRegistrationCode() {
      this.form.step1.registrationCode = '';
      this.withRegistrationCode = false;
    },
    setFocus(item) {
      this.$nextTick(() => {
        this.$refs[item].focus();
      });
    },
    async logoutAndStart() {
      await authService.logout(this.$i18n.t('urlRegister'));
      this.resetForm();
      this.cancelValidationGoBack();
    },
    resetForm() {
      this.errors = [];
      this.errorType = '';
      this.form = {
        step1: {
          username: '',
          password: '',
          repeatPassword: '',
          godfather: '',
          registrationCode: '',
        },
        step2: {
          prefix: store.state.countrySelected.prefix,
          phone: '',
        },
      };
    },
  },
};
</script>

<style lang="scss">
.v-stepper__header {
  height: 90px !important;
}
.changeLabelDirection .v-stepper__step {
  flex-direction: column !important;
}
.changeLabelDirection .v-stepper__step .v-stepper__label {
  display: inline-flex !important;
}
.v-stepper__step .v-stepper__label {
  font-weight: 500 !important;
}
.v-stepper__step__step {
  font-weight: 900 !important;
  color: #5ea4c8 !important;
}
.v-stepper__step__step .v-icon {
  font-weight: 900 !important;
  color: #5ea4c8 !important;
}
// .changeLabelDirection .v-stepper__step--error .v-icon {
//   color: #EF5350 !important
// }
</style>
